import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';

import TSTimer from '@ts-core/components/TSTimer/TSTimer';
import Typography from '@ts-core/components/Typography';
import ConfirmButton from '@ts-core/components/ConfirmButton';

import ExerciseHandler from 'modules/exercises/components/ExerciseHandler';

import { Assignment } from '../../types';

interface StudentAssignmentProps {
  assignment?: Assignment;
  loading?: boolean;

  isSubmittingAssignmentAnswer: boolean;
  onSendAssignment: (input: Record<string, any>) => void;
}

const StudentAssignment = (props: StudentAssignmentProps) => {
  const intl = useIntl();
  const { assignment, loading, isSubmittingAssignmentAnswer, onSendAssignment } = props;

  const [answers, setAnswers] = useState<Record<string, any>>({});

  const handleSelectOption = useCallback((option: any, questionId: string) => {
    setAnswers((answers) => ({ ...answers, [questionId]: option }));
  }, []);

  // Actions
  const handleSendAssignment = useCallback(() => {
    onSendAssignment(answers);
  }, [answers, onSendAssignment]);

  function renderTimer() {
    const startedAt = assignment?.answer?.startedAt;
    if (assignment?.timeLimit && startedAt) {
      return (
        <TSTimer
          timeLimit={assignment.timeLimit}
          startedAt={startedAt}
          onTimeOut={() => {
            if (!assignment?.allowAnswersAfterTimeLimit) {
              handleSendAssignment();
            }
          }}
        />
      );
    }
    return <span />;
  }

  return (
    <Box>
      {renderTimer()}
      <Typography variant="h5" mb={2} borderBottom={1} pb={1} borderColor="grey.400" loading={loading}>
        {assignment?.name}
      </Typography>

      {assignment?.exercises?.map((e) => (
        <ExerciseHandler exercise={e} key={e.id} onSelectOption={handleSelectOption} selectedOptions={answers} />
      ))}
      <Box display="flex" justifyContent="flex-end">
        <ConfirmButton
          variant="contained"
          color="primary"
          loading={isSubmittingAssignmentAnswer}
          disabled={loading}
          onConfirm={handleSendAssignment}
          alertTitle={intl.formatMessage({ id: 'studentAssignment.sendAnswers.title' })}
          alertMessage={intl.formatMessage({ id: 'studentAssignment.sendAnswers.message' })}
        >
          {intl.formatMessage({ id: 'studentAssignment.sendAnswers.button.title' })}
        </ConfirmButton>
      </Box>
    </Box>
  );
};

StudentAssignment.propTypes = {
  loading: PropTypes.bool,
  assignment: PropTypes.object,

  onSendAssignment: PropTypes.func,
  isSubmittingAssignmentAnswer: PropTypes.bool,
};

StudentAssignment.defaultProps = {
  assignment: {},

  onSendAssignment: undefined,
  loading: false,
  isSubmittingAssignmentAnswer: false,
};

export default StudentAssignment;
